import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { ArrowLeftIcon, CheckIcon, XMarkIcon, EyeIcon } from '@heroicons/react/24/outline'
import LoadingSpinner from '../components/LoadingSpinner'

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com/v1'

interface Pool {
  poolId: string
  name: string
  description: string
  status: 'active' | 'completed' | 'cancelled'
  games: Game[]
}

interface Game {
  gameId: string
  homeTeam: string
  awayTeam: string
  homeConference: string
  awayConference: string
  startDate: string
  week: number
  venue: string
  lines: Line[]
  mediaOutlets: string[]
  homeTeamRankings?: {
    ap?: { rank: number; school: string; conference: string; points: number; firstPlaceVotes: number; poll: string; } | null
    coaches?: { rank: number; school: string; conference: string; points: number; firstPlaceVotes: number; poll: string; } | null
  }
  awayTeamRankings?: {
    ap?: { rank: number; school: string; conference: string; points: number; firstPlaceVotes: number; poll: string; } | null
    coaches?: { rank: number; school: string; conference: string; points: number; firstPlaceVotes: number; poll: string; } | null
  }
  homeTeamRecord?: {
    team: string
    conference: string
    wins: number
    losses: number
    ties: number
    conferenceWins: number
    conferenceLosses: number
    conferenceTies: number
    homeWins: number
    homeLosses: number
    awayWins: number
    awayLosses: number
  } | null
  awayTeamRecord?: {
    team: string
    conference: string
    wins: number
    losses: number
    ties: number
    conferenceWins: number
    conferenceLosses: number
    conferenceTies: number
    homeWins: number
    homeLosses: number
    awayWins: number
    awayLosses: number
  } | null
}

interface Line {
  provider: string
  spread: number
  overUnder?: number
}

interface Pick {
  gameId: string
  selectedTeam: 'home' | 'away'
  pick: 'home' | 'away' // Backend uses 'pick' field
  confidence?: number
}

interface UserPicks {
  [gameId: string]: Pick
}

export default function ViewMyPicksPage() {
  const { poolId } = useParams<{ poolId: string }>()
  const { user, getAccessTokenSilently } = useAuth0()
  const [pool, setPool] = useState<Pool | null>(null)
  const [userPicks, setUserPicks] = useState<UserPicks>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (poolId) {
      fetchPoolAndPicks()
    }
  }, [poolId])

  const fetchPoolAndPicks = async () => {
    try {
      setLoading(true)
      const token = await getAccessTokenSilently()
      
      // Fetch pool details
      const poolResponse = await fetch(`${API_BASE_URL}/pools/${poolId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!poolResponse.ok) {
        throw new Error(`Failed to fetch pool: ${poolResponse.statusText}`)
      }

      const poolData = await poolResponse.json()
      setPool(poolData.pool)

      // Fetch user's existing picks
      try {
        const picksResponse = await fetch(`${API_BASE_URL}/pools/${poolId}/picks`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (picksResponse.ok) {
          const picksData = await picksResponse.json()
          const picksMap: UserPicks = {}
          picksData.picks?.forEach((pick: any) => {
            picksMap[pick.gameId] = {
              gameId: pick.gameId,
              selectedTeam: pick.pick, // Map backend 'pick' to frontend 'selectedTeam'
              pick: pick.pick,
              confidence: pick.confidence
            }
          })
          setUserPicks(picksMap)
        }
      } catch (picksError) {
        console.log('No existing picks found or error fetching picks')
      }

    } catch (err) {
      console.error('Error fetching pool and picks:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch pool data')
    } finally {
      setLoading(false)
    }
  }

  const formatGameTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getSpreadDisplay = (game: Game, team: 'home' | 'away') => {
    const dkLine = game.lines?.find(line => line.provider === 'DraftKings')
    const espnLine = game.lines?.find(line => line.provider === 'ESPN Bet')
    const spread = dkLine?.spread || espnLine?.spread

    if (spread === undefined || spread === null) return ''

    // Spread from API is always from HOME team perspective
    // For away team, we need to flip the sign
    const teamSpread = team === 'home' ? spread : -spread
    const sign = teamSpread > 0 ? '+' : ''
    return ` (${sign}${teamSpread})`
  }

  const isGameStarted = (game: Game) => {
    return new Date(game.startDate) <= new Date()
  }

  const totalPicks = Object.keys(userPicks).length
  const totalGames = pool?.games?.length || 0

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="text-sm text-red-700 dark:text-red-400">{error}</div>
        </div>
      </div>
    )
  }

  if (!pool) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Pool not found</h1>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Link 
            to={`/admin/pools/football/${poolId}`}
            className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </Link>
          <div>
            <div className="flex items-center">
              <EyeIcon className="w-6 h-6 text-purple-600 dark:text-purple-400 mr-2" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Picks</h1>
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{pool.name}</p>
          </div>
        </div>

        {/* Progress */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Picks Made
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {totalPicks} of {totalGames} games
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className="bg-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${totalGames > 0 ? (totalPicks / totalGames) * 100 : 0}%` }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3 mb-6">
          <Link
            to={`/pools/${poolId}/picks`}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Edit Picks
          </Link>
        </div>
      </div>

      {/* Games */}
      <div className="space-y-6">
        {pool.games.map((game) => {
          const gameStarted = isGameStarted(game)
          const userPick = userPicks[game.gameId]

          return (
            <div 
              key={game.gameId} 
              className={`bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 ${
                gameStarted ? 'opacity-90' : ''
              }`}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    {formatGameTime(game.startDate)} • {game.venue}
                  </div>
                  {game.mediaOutlets.length > 0 && (
                    <div className="text-sm text-blue-600 dark:text-blue-400">
                      {game.mediaOutlets.join(', ')}
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {gameStarted && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                      Game Started
                    </span>
                  )}
                  {userPick && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      <CheckIcon className="w-3 h-3 mr-1" />
                      Pick Made
                    </span>
                  )}
                  {!userPick && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                      <XMarkIcon className="w-3 h-3 mr-1" />
                      No Pick
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Away Team */}
                <div 
                  className={`p-4 rounded-lg border-2 ${
                    userPick?.selectedTeam === 'away'
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 dark:border-gray-600'
                  }`}
                >
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-1">
                      {game.awayTeamRankings?.ap?.rank && (
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full mr-2">
                          {game.awayTeamRankings.ap.rank}
                        </span>
                      )}
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {game.awayTeam}{getSpreadDisplay(game, 'away')}
                        {game.awayTeamRecord && (
                          <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">
                            ({game.awayTeamRecord.wins}-{game.awayTeamRecord.losses})
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      @ {game.awayConference}
                    </div>
                    {userPick?.selectedTeam === 'away' && (
                      <div className="mt-2">
                        <CheckIcon className="w-6 h-6 text-purple-600 dark:text-purple-400 mx-auto" />
                        <div className="text-xs text-purple-600 dark:text-purple-400 font-medium mt-1">
                          Your Pick
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Home Team */}
                <div 
                  className={`p-4 rounded-lg border-2 ${
                    userPick?.selectedTeam === 'home'
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                      : 'border-gray-200 dark:border-gray-600'
                  }`}
                >
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-1">
                      {game.homeTeamRankings?.ap?.rank && (
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full mr-2">
                          {game.homeTeamRankings.ap.rank}
                        </span>
                      )}
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {game.homeTeam}{getSpreadDisplay(game, 'home')}
                        {game.homeTeamRecord && (
                          <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">
                            ({game.homeTeamRecord.wins}-{game.homeTeamRecord.losses})
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {game.homeConference}
                    </div>
                    {userPick?.selectedTeam === 'home' && (
                      <div className="mt-2">
                        <CheckIcon className="w-6 h-6 text-purple-600 dark:text-purple-400 mx-auto" />
                        <div className="text-xs text-purple-600 dark:text-purple-400 font-medium mt-1">
                          Your Pick
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Over/Under Info */}
              {game.lines.length > 0 && (
                <div className="mt-3 text-center text-sm text-gray-600 dark:text-gray-300">
                  {game.lines.map((line, index) => (
                    <span key={index} className="mr-4">
                      {line.provider}: {line.spread > 0 ? '+' : ''}{line.spread}
                      {line.overUnder && ` • O/U ${line.overUnder}`}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Summary */}
      {totalPicks > 0 && (
        <div className="mt-8 bg-purple-50 dark:bg-purple-900/20 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-2">
            Pick Summary
          </h3>
          <div className="text-sm text-purple-700 dark:text-purple-300">
            You have made <strong>{totalPicks}</strong> out of <strong>{totalGames}</strong> possible picks.
            {totalPicks < totalGames && (
              <span className="block mt-1">
                You still need to make <strong>{totalGames - totalPicks}</strong> more pick{totalGames - totalPicks !== 1 ? 's' : ''}.
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
